/* eslint-disable no-restricted-globals */
import { useState, useEffect, useMemo } from 'react';

import VendorService from '../../services/VendorService';
import LoanService from '../../services/LoanService';
import useForm from '../utils/useForm';
import createLoanInsuranceBillLabel from '../../helpers/forms/createLoanInsuranceBillLabel';
import { INSURANCE_STATUSES } from '../../constants/formOptions';

const getSortedVendors = async () => {
  const vendors = await VendorService.getVendors({
    type: 'Insurance',
  });

  if (!vendors) {
    return [];
  }

  return vendors.sort((a, b) => {
    return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
  });
};

const percentageOptions = [
  { value: '1', label: '1%' },
  { value: '2', label: '2%' },
  { value: '3', label: '3%' },
  { value: '4', label: '4%' },
  { value: '5', label: '5%' },
  { value: '6', label: '6%' },
  { value: '7', label: '7%' },
  { value: '8', label: '8%' },
  { value: '9', label: '9%' },
  { value: '10', label: '10%' },
];

const yesNoOptions = [
  { value: true, label: 'Yes' },
  { value: false, label: 'No' },
];

const useInsuranceForm = (onSubmit, values, loanId, readOnly) => {
  const [currentVals, setCurrentVals] = useState(values);

  const fields = useMemo(
    () => [
      {
        title: 'Insurance Policy',
        fields: [
          {
            title: 'Status',
            name: 'status',
            type: 'select',
            options: INSURANCE_STATUSES,
          },
          {
            title: 'Policy Number',
            name: 'policy_number',
            type: 'text',
            required: true,
          },
          {
            title: 'Type',
            name: 'type',
            type: 'select',
            options: [
              { value: 'Hazard', label: 'Hazard' },
              { value: 'Flood', label: 'Flood' },
              { value: 'Wind', label: 'Wind' },
            ],
          },
          {
            title: 'Carrier',
            name: readOnly ? '_carrier.name' : 'vendor_id',
            type: readOnly ? 'textarea' : 'fetchableSelect',
            nullable: true,
            fetchOptions: {
              key: ['vendors', 'Insurance'],
              queryFn: getSortedVendors,
              labelKey: 'name',
            },
            afterChange: (value, setFieldValue) => {
              setFieldValue('carrier_contact_id', '');
            },
            rows: 2,
          },
          {
            title: 'Carrier Contact',
            name: readOnly ? '_carrier_contact.name' : 'carrier_contact_id',
            type: readOnly ? 'textarea' : 'fetchableSelect',
            nullable: true,
            fetchOptions: {
              key: ['carrierContacts', 'Insurance', currentVals?.vendor_id],
              queryFn: () =>
                VendorService.getVendorContacts(currentVals?.vendor_id),
              labelKey: 'name',
            },
            isHidden: readOnly
              ? (vals) => !vals?.carrier_contact_id
              : (vals) => !vals.vendor_id,
            hideIfNoOptions: true,
            rows: 2,
          },
          {
            title: 'Agent',
            name: readOnly ? '_agent.name' : 'agent_id',
            type: readOnly ? 'textarea' : 'fetchableSelect',
            nullable: true,
            fetchOptions: {
              key: ['vendors', 'Insurance'],
              queryFn: getSortedVendors,
              labelKey: 'name',
              disabled: !currentVals?.vendor_id,
            },
            afterChange: (value, setFieldValue) => {
              setFieldValue('agent_contact_id', '');
            },
            rows: 2,
          },
          {
            title: 'Agent Contact',
            name: 'agent_contact_id',
            nullable: true,
            type: 'fetchableSelect',
            fetchOptions: {
              key: ['carrierContacts', 'Insurance', currentVals?.agent_id],
              queryFn: () =>
                VendorService.getVendorContacts(currentVals?.agent_id),
              labelKey: 'name',
            },
            isHidden: (vals) => !vals.agent_id,
            hideIfNoOptions: true,
          },
          {
            title: 'Broker',
            name: 'broker_id',
            type: 'fetchableSelect',
            nullable: true,
            fetchOptions: {
              key: ['vendors', 'Insurance'],
              queryFn: getSortedVendors,
              labelKey: 'name',
              disabled: !currentVals?.vendor_id,
            },
            afterChange: (value, setFieldValue) => {
              setFieldValue('broker_contact_id', '');
            },
            rows: 2,
            isHidden: () => readOnly,
          },
          {
            title: 'Broker Contact',
            name: 'broker_contact_id',
            type: 'fetchableSelect',
            nullable: true,
            fetchOptions: {
              key: ['carrierContacts', 'Insurance', currentVals?.broker_id],
              queryFn: () =>
                VendorService.getVendorContacts(currentVals?.broker_id),
              labelKey: 'name',
            },
            isHidden: (vals) => !vals.broker_id || readOnly,
            hideIfNoOptions: true,
          },
          {
            title: 'Force Place',
            name: 'force_place',
            type: 'checkbox',
          },
        ],
      },
      {
        title: 'Payment',
        fields: [
          {
            title: 'Send payment to:',
            name: 'send_payment_to',
            type: readOnly ? 'text' : 'select',
            options: [
              { label: 'Agent', value: 'Agent' },
              { label: 'Carrier', value: 'Carrier' },
              { label: 'Broker', value: 'Broker' },
            ],
          },
          {
            title: 'Policy Start Date',
            name: 'start_date',
            type: 'date',
          },
          {
            title: 'Policy End Date',
            name: 'end_date',
            type: 'date',
          },
          {
            title: 'Premium Frequency',
            name: 'premium_frequency',
            type: 'select',
            options: [
              { value: 'annual', label: 'Annual' },
              { value: 'semi-annual', label: 'Semi-Annual' },
              { value: 'monthly', label: 'Monthly' },
            ],
          },
          {
            title: 'Premium',
            name: 'premium',
            type: 'currency',
          },
          {
            title: 'Dwelling Coverage',
            name: 'dwelling_coverage',
            type: 'currency',
          },
          {
            title: 'Other Structure Coverage',
            name: 'other_structure_coverage',
            type: 'currency',
          },
          {
            title: 'Supplemental Coverage',
            name: 'supplemental_coverage',
            type: 'currency',
          },
          {
            title: 'Deductible',
            name: 'deductible',
            type: 'select',
            options: yesNoOptions,
          },
          {
            title: 'All Other Deductible %',
            name: 'deductible_percent',
            type: 'percentage',
            isHidden: (vals) => vals.deductible === false,
          },
          {
            title: 'Deductible amount',
            name: 'all_other_deductible_amount',
            type: 'currency',
            isHidden: (vals) => vals.deductible === false,
          },
          {
            title: 'Wind/Hail Deductible',
            name: 'wind_deductible',
            type: 'select',
            options: yesNoOptions,
          },
          {
            title: 'Wind/Hail Deductible %',
            name: 'wind_deductible_percent',
            type: 'percentage',
            isHidden: (vals) => vals.wind_deductible === false,
          },
          {
            title: 'Wind/Hail Deductible amount',
            name: 'wind_deductible_amount',
            type: 'currency',
            isHidden: (vals) => vals.wind_deductible === false,
          },
          {
            title: 'Endorsement %',
            name: 'endorsement',
            type: 'select',
            options: percentageOptions,
          },
          {
            title: 'Endorsement amount',
            name: 'endorsement_amount',
            type: 'currency',
          },
          {
            title: 'Next Bill',
            name: 'next_bill_id',
            // style: { gridColumn: '1 / 3' },
            className: 'md:col-start-1 md:col-end-3',
            type: 'fetchableSelect',
            rows: 1,
            nullable: true,
            fetchOptions: {
              key: ['loanBillOptions', loanId],
              queryFn: async () => {
                const bills = await LoanService.getLoanBills({
                  loanId,
                  type: 'Insurance',
                  status: null,
                  unassociatedWithInsurance: true,
                  includeNextBillId: values?.next_bill_id,
                });

                return readOnly ? bills?.items : bills;
              },
              labelKey: createLoanInsuranceBillLabel,
            },
          },
        ],
      },
    ],
    [
      currentVals?.agent_id,
      currentVals?.broker_id,
      currentVals?.vendor_id,
      loanId,
      readOnly,
      values?.next_bill_id,
    ]
  );

  const { formik } = useForm({ fields, onSubmit, values });

  // console.log(formik);

  // Sync the form values with the state
  useEffect(() => {
    setCurrentVals(formik.values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  // Set the amount of the deductible based on the percentage and the dwelling coverage
  useEffect(() => {
    const dwellingCoverage = formik.values?.dwelling_coverage;

    if (formik.values.all_other_deductible) {
      formik.setFieldValue(
        'all_other_deductible_amount',
        `${(formik.values.all_other_deductible * dwellingCoverage) / 100}`
      );
    }

    if (formik.values.wind_hail_deductible) {
      formik.setFieldValue(
        'wind_deductible_amount',
        `${(formik.values.wind_hail_deductible * dwellingCoverage) / 100}`
      );
    }

    if (formik.values.endorsement) {
      formik.setFieldValue(
        'endorsement_amount',
        `${(formik.values.endorsement * dwellingCoverage) / 100}`
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.all_other_deductible,
    formik.values.wind_hail_deductible,
    formik.values.endorsement,
    formik.values.dwelling_coverage,
  ]);

  // Set the percentage of the deductible based on the amount and the dwelling coverage
  /* useEffect(() => {
    const isValueValid = (value) => {
      return percentageOptions.some((option) => option.value === `${value}`);
    };

    if (values?.endorsement_amount && !formik.values.endorsement) {
      const value =
        (values.endorsement_amount / formik.values.dwelling_coverage) * 100;

      if (isValueValid(value)) {
        formik.setFieldValue('endorsement', value);
      }
    }

    if (values?.wind_deductible_amount && !formik.values.wind_hail_deductible) {
      const value =
        (values.wind_deductible_amount / formik.values.dwelling_coverage) * 100;

      if (isValueValid(value)) {
        formik.setFieldValue('wind_hail_deductible', value);
      }
    }

    if (
      values?.all_other_deductible_amount &&
      !formik.values.all_other_deductible
    ) {
      const value =
        (values.all_other_deductible_amount / formik.values.dwelling_coverage) *
        100;

      if (isValueValid(value)) {
        formik.setFieldValue('all_other_deductible', value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.dwelling_coverage,
    values?.endorsement_amount,
    formik.values.endorsement,
    values?.wind_deductible_amount,
    formik.values.wind_hail_deductible,
    values?.all_other_deductible_amount,
  ]); */

  return { formik, fields, values };
};

export default useInsuranceForm;
